
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  description: string | null;
  changes: ChangeItem[];
  publishedAt: Date;
  createdAt: Date;
}

export interface ChangeItem {
  type: "feature" | "improvement" | "fix" | "breaking";
  text: string;
}

export function useChangelog() {
  const queryClient = useQueryClient();

  const useChangelogsQuery = () => useQuery({
    queryKey: ["changelogs"],
    queryFn: async (): Promise<ChangelogEntry[]> => {
      const { data, error } = await supabase
        .from("changelogs")
        .select("*")
        .order("published_at", { ascending: false });

      if (error) throw error;

      return (data || []).map(item => ({
        id: item.id,
        version: item.version,
        title: item.title,
        description: item.description,
        changes: (item.changes as unknown as ChangeItem[]) || [],
        publishedAt: new Date(item.published_at),
        createdAt: new Date(item.created_at),
      }));
    },
  });

  const useUnreadChangelogsQuery = () => useQuery({
    queryKey: ["changelogs", "unread"],
    queryFn: async (): Promise<ChangelogEntry[]> => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) return [];

      // Get all changelogs
      const { data: changelogs, error: clError } = await supabase
        .from("changelogs")
        .select("*")
        .order("published_at", { ascending: false });

      if (clError) throw clError;

      // Get user's reads
      const { data: reads, error: rError } = await supabase
        .from("changelog_reads")
        .select("changelog_id")
        .eq("user_id", userData.user.id);

      if (rError) throw rError;

      const readIds = new Set((reads || []).map(r => r.changelog_id));

      return (changelogs || [])
        .filter(cl => !readIds.has(cl.id))
        .map(item => ({
          id: item.id,
          version: item.version,
          title: item.title,
          description: item.description,
          changes: (item.changes as unknown as ChangeItem[]) || [],
          publishedAt: new Date(item.published_at),
          createdAt: new Date(item.created_at),
        }));
    },
    refetchInterval: 300000, // 5 min
  });

  const useMarkChangelogAsReadMutation = () => useMutation({
    mutationFn: async (changelogId: string) => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("changelog_reads")
        .upsert({
          user_id: userData.user.id,
          changelog_id: changelogId,
        }, { onConflict: "user_id,changelog_id" });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["changelogs", "unread"] });
    },
  });

  const useMarkAllChangelogsAsReadMutation = () => useMutation({
    mutationFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) throw new Error("Not authenticated");

      const { data: changelogs } = await supabase
        .from("changelogs")
        .select("id");

      if (!changelogs?.length) return;

      const { data: reads } = await supabase
        .from("changelog_reads")
        .select("changelog_id")
        .eq("user_id", userData.user.id);

      const readIds = new Set((reads || []).map(r => r.changelog_id));
      const unread = changelogs.filter(cl => !readIds.has(cl.id));

      if (unread.length === 0) return;

      const { error } = await supabase
        .from("changelog_reads")
        .insert(unread.map(cl => ({
          user_id: userData.user!.id,
          changelog_id: cl.id,
        })));

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["changelogs", "unread"] });
    },
  });

  return {
    useChangelogsQuery,
    useUnreadChangelogsQuery,
    useMarkChangelogAsReadMutation,
    useMarkAllChangelogsAsReadMutation,
  };
}
